import { message } from 'antd'
import {
  applyMeasurementSystemToFormField,
  assetOverviewPermissionsCheck,
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import { SimpleFormField } from 'components/form/formTypes'
import { GeckoToggleForm } from 'components/form/GeckoToggleForm'
import { AssetOverviewUXContext } from 'features/assetDatabase/contexts/AssetOverviewUXLayer'
import { useGeckoLocale } from 'features/locale'
import { useGeckoPermissions } from 'features/permissions'
import React, { useContext, useRef } from 'react'
import { SubmitHandler } from 'react-hook-form'
import {
  ExternalUserFieldAccess,
  GeckoComponent,
  getFormFieldByDataKey,
  InputType,
  RolodexEntryType,
} from 'types'
import { requiredStringSchema } from 'utils/zodHelpers'

import { ComponentCard } from './ComponentCard'

interface NonAMComponentCardProps {
  component: GeckoComponent
  modelViewer?: React.ReactNode
  forceRefresh: () => Promise<void>
}

export const NonAMComponentCard = (props: NonAMComponentCardProps) => {
  const { component, modelViewer, forceRefresh } = props
  const [messageApi, contextHolder] = message.useMessage()
  const { permissions } = useGeckoPermissions()
  const { measurementSystem } = useGeckoLocale()
  const formRef = useRef<HTMLFormElement>(null)
  const {
    inResolveMode,
    inEditMode,
    handleSaveToRolodex,
    handleDeleteInRolodex,
  } = useContext(AssetOverviewUXContext)
  const componentID = component.id
  const componentName = component.data.name
  const currentlyEditingComponent = inEditMode.includes(componentID)

  const formFieldsByUniqueID = [
    {
      uniqueID: componentID,
      formFields: [
        {
          dataKey: 'name',
          label: 'Name',
          value: componentName,
          unit_magnitude: null,
          validationRule: requiredStringSchema,
          externalUserAccess: ExternalUserFieldAccess.ReadWrite,
          isNeededForDerivedLayer: false,
          inputType: InputType.Text,
        },
      ],
    },
  ]

  const componentCardFormOnSubmit: SubmitHandler<SimpleFormField> = (data) => {
    const convertedFormFields = createEditedObject(
      data,
      createUniqueFormFields(formFieldsByUniqueID)
    )
    const name = getFormFieldByDataKey(
      convertedFormFields[0].formFields,
      'name'
    )
    handleSaveToRolodex(
      [
        {
          id: componentID,
          type: RolodexEntryType.COMPONENT,
          data: {
            name: name?.value ? name?.value.toString() : '',
          },
          tags: { ...component.tags },
          index: {},
          generation: 0,
          updated_at: '',
        },
      ],
      messageApi,
      forceRefresh,
      componentID
    )
  }

  const handleDelete = () => {
    handleDeleteInRolodex(componentID, messageApi, forceRefresh)
  }

  return (
    <ComponentCard
      componentID={componentID}
      componentName={componentName}
      ref={formRef}
      handleDelete={handleDelete}
      deleteMessage={
        'Are you sure you want to delete this component? This will untag the component from any inspections or units. This cannot be undone.'
      }
      modelViewer={modelViewer}
      infoTable={
        <>
          {contextHolder}
          {permissions && (
            <GeckoToggleForm
              formFieldsByUniqueID={formFieldsByUniqueID}
              inEditMode={currentlyEditingComponent}
              inResolveMode={inResolveMode}
              permissionsCheck={{
                userPermissions: permissions,
                permissionsChecker: assetOverviewPermissionsCheck,
              }}
              measurementApplication={{
                measurementApplication: applyMeasurementSystemToFormField,
                measurementSystem: measurementSystem,
              }}
              onSubmit={componentCardFormOnSubmit}
              ref={formRef}
            />
          )}
        </>
      }
    />
  )
}
